import { z } from 'zod';
/** Report date presets. `all` is deliberate: it must always be chosen explicitly, never defaulted. */
export const SMART_REPORT_DATE_PRESETS = ['today', 'yesterday', 'last7', 'last30', 'thisMonth', 'lastMonth', 'custom', 'all'];
/** Longest custom range accepted (days), to keep one request bounded. */
export const SMART_REPORT_MAX_CUSTOM_DAYS = 1830;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
export const smartReportDateSchema = z
    .object({
    preset: z.enum(SMART_REPORT_DATE_PRESETS),
    from: z.string().regex(ISO_DATE, 'Use YYYY-MM-DD.').optional(),
    to: z.string().regex(ISO_DATE, 'Use YYYY-MM-DD.').optional(),
})
    .strict();
export class SmartReportRangeError extends Error {
    constructor(message) {
        super(message);
        this.name = 'SmartReportRangeError';
    }
}
function zonedParts(t, tz) {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: tz, hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric',
    }).formatToParts(new Date(t));
    const get = (type) => Number(parts.find((p) => p.type === type)?.value);
    return { year: get('year'), month: get('month'), day: get('day'), hour: get('hour'), minute: get('minute') };
}
function offsetMs(t, tz) {
    const p = zonedParts(t, tz);
    return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute) - Math.floor(t / 60000) * 60000;
}
function parseIsoDate(iso) {
    if (!ISO_DATE.test(iso))
        throw new SmartReportRangeError(`Invalid date "${iso}". Use YYYY-MM-DD.`);
    const [y, m, d] = iso.split('-').map(Number);
    const check = new Date(Date.UTC(y, m - 1, d));
    if (check.getUTCFullYear() !== y || check.getUTCMonth() !== m - 1 || check.getUTCDate() !== d) {
        throw new SmartReportRangeError(`Invalid calendar date "${iso}".`);
    }
    return { year: y, month: m, day: d };
}
function toIso(ms) {
    return new Date(ms).toISOString().slice(0, 10);
}
function addDays(iso, n) {
    const { year, month, day } = parseIsoDate(iso);
    return toIso(Date.UTC(year, month - 1, day + n));
}
function dayDiff(a, b) {
    const pa = parseIsoDate(a);
    const pb = parseIsoDate(b);
    return Math.round((Date.UTC(pb.year, pb.month - 1, pb.day) - Date.UTC(pa.year, pa.month - 1, pa.day)) / 86400000);
}
/** The UTC instant at which calendar day `iso` begins in time zone `tz` (DST-aware). */
export function localMidnightUtc(iso, tz) {
    const { year, month, day } = parseIsoDate(iso);
    const naive = Date.UTC(year, month - 1, day);
    let t = naive - offsetMs(naive, tz);
    t = naive - offsetMs(t, tz);
    return new Date(t);
}
function localToday(now, tz) {
    const p = zonedParts(now.getTime(), tz);
    return toIso(Date.UTC(p.year, p.month - 1, p.day));
}
function bounded(fromDate, toDate, tz) {
    return {
        start: localMidnightUtc(fromDate, tz),
        endExclusive: localMidnightUtc(addDays(toDate, 1), tz),
        fromDate,
        toDate,
        isAllHistory: false,
    };
}
/**
 * Resolve a preset into a half-open [start, endExclusive) UTC range using the
 * business time zone, so "today" and day boundaries follow local calendar days.
 */
export function resolveReportDateRange(input, now, tz) {
    const today = localToday(now, tz);
    switch (input.preset) {
        case 'today':
            return bounded(today, today, tz);
        case 'yesterday': {
            const y = addDays(today, -1);
            return bounded(y, y, tz);
        }
        case 'last7':
            return bounded(addDays(today, -6), today, tz);
        case 'last30':
            return bounded(addDays(today, -29), today, tz);
        case 'thisMonth':
            return bounded(`${today.slice(0, 7)}-01`, today, tz);
        case 'lastMonth': {
            const firstThis = `${today.slice(0, 7)}-01`;
            const lastPrev = addDays(firstThis, -1);
            return bounded(`${lastPrev.slice(0, 7)}-01`, lastPrev, tz);
        }
        case 'custom': {
            if (!input.from || !input.to)
                throw new SmartReportRangeError('Custom range needs both a from and a to date.');
            parseIsoDate(input.from);
            parseIsoDate(input.to);
            if (input.from > input.to)
                throw new SmartReportRangeError('The from date must not be after the to date.');
            if (dayDiff(input.from, input.to) + 1 > SMART_REPORT_MAX_CUSTOM_DAYS) {
                throw new SmartReportRangeError(`Custom range is limited to ${SMART_REPORT_MAX_CUSTOM_DAYS} days.`);
            }
            return bounded(input.from, input.to, tz);
        }
        case 'all':
            return { start: null, endExclusive: null, fromDate: null, toDate: null, isAllHistory: true };
    }
}
//# sourceMappingURL=smart-report.js.map